import { createSelector } from 'reselect'
import queryString from 'query-string'
import getRowsOfTiles from './getRowsOfTiles'
import getVenuesSettings from './getVenuesSettings'

const venuesSettings = getVenuesSettings()

export const determineFiltersIsOpen = createSelector(
  state => state.router.params,
  (params) => {
    const { first, second } = params
    if (first === 'refine' || second === 'refine') {
      return true
    }
    return false
  },
)

export const determineSelectedView = createSelector(
  state => state.router.params,
  (params) => {
    const { first } = params
    if (first === 'list' || first === 'map') {
      return first
    }
    return 'tiles'
  },
)

export const buildFiltersClassName = createSelector(
  state => state.app.y,
  state => state.app.pageHeaderHeight,
  state => state.app.windowIsOnUpswing,
  (y, pageHeaderHeight, windowIsOnUpswing) => {
    let className = 'filters'
    if (windowIsOnUpswing && y > 0) {
      className += ' page-header-is-stuck'
    }
    else if (y > pageHeaderHeight) {
      className += ' is-stuck'
    }
    return className
  },
)
export const buildHeaderClassName = createSelector(
  state => state.app.y,
  state => state.app.pageHeaderHeight,
  state => state.app.windowIsOnUpswing,
  determineSelectedView,
  determineFiltersIsOpen,
  (y, pageHeaderHeight, windowIsOnUpswing, selectedView, filtersIsOpen) => {
    let className = 'header'
    if (windowIsOnUpswing && y > 0) {
      className += ' page-header-is-stuck'
    }
    else if (y > pageHeaderHeight) {
      className += ' is-stuck'
    }
    if (selectedView === 'map') {
      className += ' is-in-map-view'
    }
    if (filtersIsOpen) {
      className += ' filters-is-open'
    }
    return className
  },
)

export const buildMainClassName = createSelector(
  determineFiltersIsOpen,
  state => state.app.y,
  state => state.app.pageHeaderHeight,
  state => state.app.windowIsOnUpswing,
  (filtersIsOpen, y, pageHeaderHeight, windowIsOnUpswing) => {
    let className = 'main'
    if (filtersIsOpen) {
      className += ' filters-is-open'
    }
    if (windowIsOnUpswing && y > 0) {
      className += ' page-header-is-stuck'
    }
    else if (y > pageHeaderHeight) {
      className += ' is-stuck'
    }
    return className
  },
)

export const buildRefineHref = createSelector(
  determineFiltersIsOpen,
  determineSelectedView,
  state => state.router.query,
  (filtersIsOpen, selectedView, query) => {
    let qs = queryString.stringify(query)
    if (qs !== '') {
      qs = `?${qs}`
    }
    if (filtersIsOpen) {
      if (selectedView === 'list' || selectedView === 'map') {
        return `/${selectedView}${qs}`
      }
      return `/${qs}`
    }
    if (selectedView === 'list' || selectedView === 'map') {
      return `/${selectedView}/refine${qs}`
    }
    return `/refine${qs}`
  },
)

export const buildViewHrefs = createSelector(
  determineFiltersIsOpen,
  state => state.router.query,
  (filtersIsOpen, query) => {
    let qs = queryString.stringify(query)
    if (qs !== '') {
      qs = `?${qs}`
    }
    if (filtersIsOpen) {
      return {
        tiles: `/refine${qs}`,
        list: `/list/refine${qs}`,
        map: `/map/refine${qs}`,
      }
    }
    return {
      tiles: `/${qs}`,
      list: `/list${qs}`,
      map: `/map${qs}`,
    }
  },
)

const buildBaseFilterOptionHref = createSelector(
  determineSelectedView,
  (selectedView) => {
    if (selectedView === 'list') {
      return '/list/refine'
    }
    if (selectedView === 'map') {
      return '/map/refine'
    }
    return '/refine'
  },
)

const buildBaseSortOptionHref = createSelector(
  determineFiltersIsOpen,
  determineSelectedView,
  (filtersIsOpen, selectedView) => {
    if (filtersIsOpen) {
      switch (selectedView) {
        case 'tiles':
          return '/refine'
        case 'list':
          return '/list/refine'
        case 'map':
          return '/map/refine'
        default:
          break
      }
    }
    switch (selectedView) {
      case 'tiles':
        return '/'
      case 'list':
        return '/list'
      case 'map':
        return '/map'
      default:
        break
    }
  },
)

export const buildClearAllHref = createSelector(
  state => state.router.query,
  buildBaseFilterOptionHref,
  (query, baseFilterOptionHref) => {
    // Don't display Clear All link if there aren't any filters selected.
    let filterIsSelected = false
    Object.keys(venuesSettings.filterOptionsFromEs).forEach((key) => {
      if (query[key] !== undefined) {
        filterIsSelected = true
      }
    })
    if (!filterIsSelected) {
      return null
    }
    // If we've got a sort, clear all of the filters but continue to include the
    // sort in the querystring.
    if (query.sort !== undefined) {
      const qs = queryString.stringify({
        sort: query.sort,
      })
      return `${baseFilterOptionHref}?${qs}`
    }
    // Otherwise, just return the baseFilterOptionHref which will keep the UI
    // the same while clearing all filters.
    return baseFilterOptionHref
  },
)

export const buildFilterClearHref = createSelector(
  state => state.router.query,
  buildBaseFilterOptionHref,
  (state, props) => props.esKey,
  (query, baseFilterOptionHref, esKey) => {
    const qs = queryString.stringify({
      ...query,
      [esKey]: undefined,
    })
    return `${baseFilterOptionHref}?${qs}`
  },
)

const getSelected = (query, esKey) => {
  if (query[esKey] === undefined) {
    return []
  }
  if (!Array.isArray(query[esKey])) {
    return [query[esKey]]
  }
  return query[esKey]
}

const getOptions = (
  esKey,
  optionsFromEs,
  selected,
  query,
  baseFilterOptionHref,
  isSingleSelect = false,
  search = null,
) => optionsFromEs.filter((option) => {
  if (search === null) {
    return true
  }
  // Accommodate searches like "Frank Gehry"
  let found = false
  search.toLowerCase().split(' ').forEach((searchChunk) => {
    if (!found) {
      found = option.toLowerCase().indexOf(searchChunk) !== -1
    }
  })
  return found
}).map((option) => {
  let isChecked
  let qs
  let mutableOption = option
  if (mutableOption.value === undefined) {
    mutableOption = {
      name: option,
      value: option,
    }
  }
  // If the option isn't in the selected list,
  if (selected.indexOf(mutableOption.value) === -1) {
    // it shouldn't be checked.
    isChecked = false
    // Clicks on this option should add it to already-selected options (this is
    // the multi-select behavior).
    qs = queryString.stringify({
      ...query,
      [esKey]: [...selected, mutableOption.value],
    })
  }
  else {
    // The option is selected, so mark it with a check.
    isChecked = true
    // Clicks on this option should remove it from the selected options list.
    qs = queryString.stringify({
      ...query,
      [esKey]: selected.filter(value => value !== mutableOption.value),
    })
  }
  // Is this a single-select?
  if (isSingleSelect) {
    // If it isn't selected,
    if (!isChecked) {
      // add it to the selected list, replacing anything that was there.
      qs = queryString.stringify({
        ...query,
        [esKey]: [mutableOption.value],
      })
    }
    // It isn't selected, so
    else {
      // treat the option like a toggle and remove it from the selected list.
      qs = queryString.stringify({
        ...query,
        [esKey]: [],
      })
    }
  }
  // Remove trailing ? from URLs with no querystring params.
  let href = `${baseFilterOptionHref}?${qs}`
  if (href.slice(-1) === '?') {
    href = href.replace('?', '')
  }
  return {
    href,
    isChecked,
    name: mutableOption.name,
    value: mutableOption.value,
  }
})

export const getArchitectsOptions = createSelector(
  state => state.router.query,
  buildBaseFilterOptionHref,
  state => state.filter.searches.architects,
  (query, baseFilterOptionHref, search) => getOptions(
    'architects',
    venuesSettings.filterOptionsFromEs.architects,
    getSelected(query, 'architects'),
    query,
    baseFilterOptionHref,
    true,
    search,
  ),
)

export const getArupServicesOptions = createSelector(
  state => state.router.query,
  buildBaseFilterOptionHref,
  (query, baseFilterOptionHref) => getOptions(
    'arup_services',
    venuesSettings.filterOptionsFromEs.arup_services,
    getSelected(query, 'arup_services'),
    query,
    baseFilterOptionHref,
  ),
)

export const getCountryOptions = createSelector(
  state => state.router.query,
  buildBaseFilterOptionHref,
  state => state.filter.searches.country,
  (query, baseFilterOptionHref, search) => getOptions(
    'country',
    venuesSettings.filterOptionsFromEs.country,
    getSelected(query, 'country'),
    query,
    baseFilterOptionHref,
    true,
    search,
  ),
)

export const getFilterSearch = createSelector(
  (state, props) => props.esKey,
  state => state.filter.searches,
  (esKey, searches) => searches[esKey],
)

export const getHallUsesOptions = createSelector(
  state => state.router.query,
  buildBaseFilterOptionHref,
  (query, baseFilterOptionHref) => getOptions(
    'hall_uses',
    venuesSettings.filterOptionsFromEs.hall_uses,
    getSelected(query, 'hall_uses'),
    query,
    baseFilterOptionHref,
  ),
)

export const getInOutdoorOptions = createSelector(
  state => state.router.query,
  buildBaseFilterOptionHref,
  (query, baseFilterOptionHref) => getOptions(
    'in_outdoor',
    venuesSettings.filterOptionsFromEs.in_outdoor,
    getSelected(query, 'in_outdoor'),
    query,
    baseFilterOptionHref,
  ),
)

export const getProjectTypesOptions = createSelector(
  state => state.router.query,
  buildBaseFilterOptionHref,
  (query, baseFilterOptionHref) => getOptions(
    'project_types',
    venuesSettings.filterOptionsFromEs.project_types,
    getSelected(query, 'project_types'),
    query,
    baseFilterOptionHref,
  ),
)

export const getSeatCountOptions = createSelector(
  state => state.router.query,
  buildBaseFilterOptionHref,
  (query, baseFilterOptionHref) => getOptions(
    'seat_count',
    venuesSettings.filterOptionsFromEs.seat_count,
    getSelected(query, 'seat_count'),
    query,
    baseFilterOptionHref,
  ),
)

export const getSortOptions = createSelector(
  state => state.router.query,
  buildBaseSortOptionHref,
  (query, baseSortOptionHref) => {
    const selected = getSelected(query, 'sort')
    return ['Newest first', 'Alphabetical'].map((option) => {
      let isChecked = false
      let qs
      if (option === 'Newest first') {
        if (selected.length === 0) {
          isChecked = true
        }
        qs = queryString.stringify({
          ...query,
          sort: [],
        })
      }
      else {
        if (selected.indexOf(option) !== -1) {
          isChecked = true
        }
        qs = queryString.stringify({
          ...query,
          sort: [option],
        })
      }
      let href = `${baseSortOptionHref}?${qs}`
      if (href.slice(-1) === '?') {
        href = href.replace('?', '')
      }
      return {
        href,
        isChecked,
        name: option,
        value: option,
      }
    })
  },
)

export const getYearOptions = createSelector(
  state => state.router.query,
  buildBaseFilterOptionHref,
  (query, baseFilterOptionHref) => getOptions(
    'year',
    venuesSettings.filterOptionsFromEs.year,
    getSelected(query, 'year'),
    query,
    baseFilterOptionHref,
  ),
)

export const transformVenuesIntoRowsOfTiles = createSelector(
  state => state.venue.venues,
  getRowsOfTiles,
)
